import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { 
  ArrowLeft, Shield, Users, CheckCircle, XCircle, Clock, 
  GraduationCap, Star, Loader2, AlertCircle, Search, Eye, Book, BarChart3, QrCode, MessageSquare, LogOut
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import EdpiesLogo from '@/components/EdpiesLogo';
import BrandAssetsCard from '@/components/admin/BrandAssetsCard';
import ShowcaseSeedCard from '@/components/admin/ShowcaseSeedCard';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminDashboard = ({ updateProgress }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [pendingTutors, setPendingTutors] = useState([]);
  const [allTutors, setAllTutors] = useState([]);
  const [selectedTutor, setSelectedTutor] = useState(null);
  const [rejectionReason, setRejectionReason] = useState('');
  const [processingId, setProcessingId] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [isAdmin, setIsAdmin] = useState(false);

  const handleLogout = () => {
    localStorage.clear();
    toast.success('Logged out successfully');
    window.location.replace('/');
  };

  useEffect(() => {
    checkAdminStatus();
  }, []);

  const checkAdminStatus = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        navigate('/auth');
        return;
      }

      const response = await axios.get(`${API}/admin/check`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (!response.data.isAdmin) {
        toast.error('Admin access required');
        navigate('/');
        return;
      }

      setIsAdmin(true);
      loadData();
    } catch (error) {
      console.error('Admin check failed:', error);
      toast.error('Access denied');
      navigate('/');
    }
  };

  const loadData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      
      const [pendingRes, allRes] = await Promise.all([
        axios.get(`${API}/admin/pending-tutors`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/admin/all-tutors`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      
      setPendingTutors(pendingRes.data.pendingTutors || []);
      setAllTutors(allRes.data.tutors || []);
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Failed to load tutor data');
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (tutorId) => {
    setProcessingId(tutorId);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/admin/approve-tutor/${tutorId}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Tutor approved successfully!');
      loadData();
      setSelectedTutor(null);
    } catch (error) {
      toast.error('Failed to approve tutor');
    } finally {
      setProcessingId(null);
    }
  };

  const handleReject = async (tutorId) => {
    if (!rejectionReason.trim()) {
      toast.error('Please provide a reason for rejection');
      return;
    }
    
    setProcessingId(tutorId);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/admin/reject-tutor/${tutorId}`, {
        reason: rejectionReason
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Tutor rejected');
      loadData();
      setSelectedTutor(null);
      setRejectionReason('');
    } catch (error) {
      toast.error('Failed to reject tutor');
    } finally {
      setProcessingId(null);
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'approved':
        return <Badge className="bg-green-500"><CheckCircle className="h-3 w-3 mr-1" /> Approved</Badge>;
      case 'rejected':
        return <Badge className="bg-red-500"><XCircle className="h-3 w-3 mr-1" /> Rejected</Badge>;
      default:
        return <Badge className="bg-yellow-500"><Clock className="h-3 w-3 mr-1" /> Pending</Badge>;
    }
  };

  const getInitials = (name) => {
    return name?.split(' ').map(n => n[0]).join('').toUpperCase() || '?';
  };

  const filteredAllTutors = allTutors.filter(tutor => 
    tutor.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    tutor.email?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (!isAdmin) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
        <Loader2 className="h-8 w-8 animate-spin text-white" />
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
      {/* Header */}
      <header className="bg-white/10 backdrop-blur-sm shadow-lg border-b border-white/20">
        <div className="max-w-7xl mx-auto px-4 py-4">
          {/* Top row - always visible */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 md:gap-4">
              <Button variant="ghost" size="sm" onClick={() => navigate('/')} className="text-white hover:bg-white/20 px-2 md:px-4">
                <ArrowLeft className="h-4 w-4 md:mr-2" />
                <span className="hidden md:inline">Home</span>
              </Button>
              <div className="flex items-center gap-2">
                <Shield className="h-5 w-5 md:h-6 md:w-6 text-white" />
                <h1 className="text-lg md:text-2xl font-bold text-white">Admin</h1>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={handleLogout}
                className="text-white hover:bg-white/20 px-2 md:px-4"
                data-testid="admin-logout-btn"
              >
                <LogOut className="h-4 w-4 md:mr-2" />
                <span className="hidden md:inline">Logout</span>
              </Button>
              <EdpiesLogo size="sm" />
            </div>
          </div>
          
          {/* Navigation row - scrollable on mobile */}
          <div className="flex items-center gap-2 mt-3 overflow-x-auto pb-2 scrollbar-hide" style={{ WebkitOverflowScrolling: 'touch' }}>
            <Button 
              size="sm"
              variant="outline" 
              onClick={() => navigate('/admin/users')}
              className="bg-white/10 border-white/30 text-white hover:bg-white/20 flex-shrink-0"
            >
              <Users className="h-4 w-4 mr-1" />
              Users
            </Button>
            <Button 
              size="sm"
              variant="outline" 
              onClick={() => navigate('/admin/analytics')}
              className="bg-white/10 border-white/30 text-white hover:bg-white/20 flex-shrink-0"
            >
              <BarChart3 className="h-4 w-4 mr-1" />
              Analytics
            </Button>
            <Button 
              size="sm"
              variant="outline" 
              onClick={() => navigate('/admin/feedback')}
              className="bg-white/10 border-white/30 text-white hover:bg-white/20 flex-shrink-0"
            >
              <MessageSquare className="h-4 w-4 mr-1" />
              Feedback
            </Button>
            <Button 
              size="sm"
              variant="outline" 
              onClick={() => navigate('/admin/question-banks')}
              className="bg-white/10 border-white/30 text-white hover:bg-white/20 flex-shrink-0"
            >
              <Book className="h-4 w-4 mr-1" />
              Questions
            </Button>
            <Button 
              size="sm"
              variant="outline" 
              onClick={() => navigate('/qr-code')}
              className="bg-white/10 border-white/30 text-white hover:bg-white/20 flex-shrink-0"
            >
              <QrCode className="h-4 w-4 mr-1" />
              QR Codes
            </Button>
            <Button 
              size="sm"
              variant="outline" 
              onClick={handleLogout}
              className="bg-white/10 border-white/30 text-white hover:bg-white/20 flex-shrink-0 md:hidden"
            >
              <LogOut className="h-4 w-4 mr-1" />
              Logout
            </Button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto p-4 md:p-8">
        {/* Stats Cards */}
        <div className="grid md:grid-cols-3 gap-6 mb-8">
          <Card className="shadow-xl">
            <CardContent className="p-6 flex items-center gap-4">
              <div className="bg-yellow-100 p-4 rounded-full">
                <Clock className="h-8 w-8 text-yellow-600" />
              </div>
              <div>
                <p className="text-3xl font-bold">{pendingTutors.length}</p>
                <p className="text-gray-600">Pending Approval</p>
              </div>
            </CardContent>
          </Card>
          <Card className="shadow-xl">
            <CardContent className="p-6 flex items-center gap-4">
              <div className="bg-green-100 p-4 rounded-full">
                <CheckCircle className="h-8 w-8 text-green-600" />
              </div>
              <div>
                <p className="text-3xl font-bold">
                  {allTutors.filter(t => t.tutor_profile?.approval_status === 'approved').length}
                </p>
                <p className="text-gray-600">Approved Tutors</p>
              </div>
            </CardContent>
          </Card>
          <Card className="shadow-xl">
            <CardContent className="p-6 flex items-center gap-4">
              <div className="bg-purple-100 p-4 rounded-full">
                <Users className="h-8 w-8 text-purple-600" />
              </div>
              <div>
                <p className="text-3xl font-bold">{allTutors.length}</p>
                <p className="text-gray-600">Total Tutors</p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Brand assets — admin-only logo download */}
        <BrandAssetsCard />

        {/* Marketing showcase — one-tap seed for the 4 demo accounts */}
        <div className="mt-6">
          <ShowcaseSeedCard />
        </div>

        {loading ? (
          <div className="flex justify-center items-center py-20">
            <Loader2 className="h-8 w-8 animate-spin text-white" />
            <span className="ml-3 text-white text-lg">Loading...</span>
          </div>
        ) : (
          <Tabs defaultValue="pending" className="w-full">
            <TabsList className="grid w-full grid-cols-2 mb-6">
              <TabsTrigger value="pending">
                Pending Approval ({pendingTutors.length})
              </TabsTrigger>
              <TabsTrigger value="all">All Tutors ({allTutors.length})</TabsTrigger>
            </TabsList>

            {/* Pending Tab */}
            <TabsContent value="pending">
              {pendingTutors.length === 0 ? (
                <Card className="shadow-xl">
                  <CardContent className="p-12 text-center">
                    <CheckCircle className="h-16 w-16 text-green-300 mx-auto mb-4" />
                    <h3 className="text-2xl font-bold text-gray-700 mb-2">All Caught Up!</h3>
                    <p className="text-gray-500">No pending tutor applications to review.</p>
                  </CardContent>
                </Card>
              ) : (
                <div className="grid gap-6">
                  {pendingTutors.map((tutor) => {
                    const profile = tutor.tutor_profile || {};
                    return (
                      <Card key={tutor.id} className="shadow-xl">
                        <CardContent className="p-6">
                          <div className="flex flex-col md:flex-row gap-6">
                            {/* Tutor Avatar & Basic Info */}
                            <div className="flex items-start gap-4">
                              <div className="w-16 h-16 rounded-full bg-gradient-to-br from-purple-600 to-indigo-600 flex items-center justify-center text-2xl font-bold text-white">
                                {getInitials(tutor.name)}
                              </div>
                              <div>
                                <h3 className="text-xl font-bold text-gray-800">{tutor.name}</h3>
                                <p className="text-gray-500">{tutor.email}</p>
                                <p className="text-sm text-gray-400">
                                  Submitted: {new Date(profile.submitted_at || tutor.createdAt).toLocaleDateString()}
                                </p>
                              </div>
                            </div>

                            {/* Profile Details */}
                            <div className="flex-1 space-y-3">
                              {profile.bio && (
                                <div>
                                  <Label className="text-gray-500 text-xs">Bio</Label>
                                  <p className="text-gray-700 text-sm">{profile.bio}</p>
                                </div>
                              )}

                              <div className="grid md:grid-cols-3 gap-3 text-sm">
                                <div>
                                  <Label className="text-gray-500 text-xs">Experience</Label>
                                  <p className="font-medium">{profile.experience_years || 0} years</p>
                                </div>
                                <div>
                                  <Label className="text-gray-500 text-xs">Hourly Rate</Label>
                                  <p className="font-medium">£{profile.hourly_rate || 0}/hour</p>
                                </div>
                                <div>
                                  <Label className="text-gray-500 text-xs">Availability</Label>
                                  <p className="font-medium">{profile.availability || 'Not specified'}</p>
                                </div>
                              </div>

                              {profile.subjects?.length > 0 && (
                                <div>
                                  <Label className="text-gray-500 text-xs">Subjects</Label>
                                  <div className="flex flex-wrap gap-2 mt-1">
                                    {profile.subjects.map((subject, idx) => (
                                      <Badge key={idx} variant="secondary" className="bg-purple-100 text-purple-700">
                                        {subject}
                                      </Badge>
                                    ))}
                                  </div>
                                </div>
                              )}

                              {profile.qualifications?.length > 0 && (
                                <div>
                                  <Label className="text-gray-500 text-xs">Qualifications</Label>
                                  <div className="flex flex-wrap gap-2 mt-1">
                                    {profile.qualifications.map((qual, idx) => (
                                      <Badge key={idx} variant="outline">
                                        <GraduationCap className="h-3 w-3 mr-1" />
                                        {qual}
                                      </Badge>
                                    ))}
                                  </div>
                                </div>
                              )}
                            </div>

                            {/* Action Buttons */}
                            <div className="flex flex-col gap-2 min-w-[150px]">
                              <Button
                                onClick={() => handleApprove(tutor.id)}
                                disabled={processingId === tutor.id}
                                className="bg-green-600 hover:bg-green-700"
                              >
                                {processingId === tutor.id ? (
                                  <Loader2 className="h-4 w-4 animate-spin" />
                                ) : (
                                  <>
                                    <CheckCircle className="h-4 w-4 mr-2" />
                                    Approve
                                  </>
                                )}
                              </Button>
                              <Button
                                variant="outline"
                                onClick={() => setSelectedTutor(tutor)}
                                className="border-red-300 text-red-600 hover:bg-red-50"
                              >
                                <XCircle className="h-4 w-4 mr-2" />
                                Reject
                              </Button>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>
              )}
            </TabsContent>

            {/* All Tutors Tab */}
            <TabsContent value="all">
              <Card className="shadow-xl mb-6">
                <CardContent className="p-4">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                    <Input
                      placeholder="Search by name or email..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="pl-10"
                    />
                  </div>
                </CardContent>
              </Card>

              {filteredAllTutors.length === 0 ? (
                <Card className="shadow-xl">
                  <CardContent className="p-12 text-center">
                    <Users className="h-16 w-16 text-gray-300 mx-auto mb-4" />
                    <h3 className="text-2xl font-bold text-gray-700 mb-2">No Tutors Found</h3>
                    <p className="text-gray-500">No tutors match your search criteria.</p>
                  </CardContent>
                </Card>
              ) : (
                <div className="grid gap-4">
                  {filteredAllTutors.map((tutor) => {
                    const profile = tutor.tutor_profile || {};
                    return (
                      <Card key={tutor.id} className="shadow-lg hover:shadow-xl transition-shadow">
                        <CardContent className="p-4">
                          <div className="flex items-center gap-4">
                            <div className="w-12 h-12 rounded-full bg-gradient-to-br from-purple-600 to-indigo-600 flex items-center justify-center text-lg font-bold text-white">
                              {getInitials(tutor.name)}
                            </div>
                            <div className="flex-1">
                              <div className="flex items-center gap-2">
                                <h3 className="font-bold text-gray-800">{tutor.name}</h3>
                                {getStatusBadge(profile.approval_status)}
                              </div>
                              <p className="text-sm text-gray-500">{tutor.email}</p>
                              <div className="flex flex-wrap gap-1 mt-1">
                                {(profile.subjects || []).slice(0, 3).map((subject, idx) => (
                                  <Badge key={idx} variant="outline" className="text-xs">
                                    {subject}
                                  </Badge>
                                ))}
                              </div>
                            </div>
                            <div className="text-right text-sm text-gray-500">
                              <p>{profile.experience_years || 0} yrs exp</p>
                              <p>£{profile.hourly_rate || 0}/hr</p>
                            </div>
                            {profile.approval_status === 'pending' && (
                              <div className="flex gap-2">
                                <Button
                                  size="sm"
                                  onClick={() => handleApprove(tutor.id)}
                                  disabled={processingId === tutor.id}
                                  className="bg-green-600 hover:bg-green-700"
                                >
                                  Approve
                                </Button>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => setSelectedTutor(tutor)}
                                  className="border-red-300 text-red-600"
                                >
                                  Reject
                                </Button>
                              </div>
                            )}
                          </div>
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>
              )}
            </TabsContent>
          </Tabs>
        )}
      </div>

      {/* Rejection Modal */}
      {selectedTutor && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <Card className="w-full max-w-md">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-red-600">
                <XCircle className="h-5 w-5" />
                Reject Tutor Application
              </CardTitle>
              <CardDescription>
                Rejecting {selectedTutor.name}&apos;s application
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                <div className="flex items-start gap-2">
                  <AlertCircle className="h-5 w-5 text-yellow-600 flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-yellow-700">
                    Please provide a clear reason for rejection. This will be shown to the tutor so they can improve their application.
                  </p>
                </div>
              </div>

              <div className="space-y-2">
                <Label>Reason for Rejection</Label>
                <Textarea
                  value={rejectionReason}
                  onChange={(e) => setRejectionReason(e.target.value)}
                  placeholder="e.g., Incomplete profile, missing qualifications, needs more detail..."
                  rows={4}
                />
              </div>

              <div className="flex gap-3">
                <Button
                  variant="outline"
                  onClick={() => {
                    setSelectedTutor(null);
                    setRejectionReason('');
                  }}
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button
                  onClick={() => handleReject(selectedTutor.id)}
                  disabled={processingId === selectedTutor.id || !rejectionReason.trim()}
                  className="flex-1 bg-red-600 hover:bg-red-700"
                >
                  {processingId === selectedTutor.id ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    'Confirm Rejection'
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default AdminDashboard;
